import type { LucideIcon } from "lucide-react";
import {
  Bell,
  ClipboardCheck,
  FileText,
  Home,
  KeyRound,
  Monitor,
  ScrollText,
  Users,
  Workflow,
} from "lucide-react";

import { ROUTES } from "@/app/routes";
import type { HttpMethod } from "@/auth/permissions/permission-targets";
import { PERMISSION_TARGETS } from "@/auth/permissions/permission-targets";

export type NavItemStatus =
  /** 実装済み。リンクとして機能する */
  | "enabled"
  /** 未実装。表示するが aria-disabled にして「準備中」バッジを付ける */
  | "planned"
  /** 表示しない */
  | "hidden";

export type NavItem = {
  /** i18n キー（nav:items.<key>）と安定 ID を兼ねる */
  readonly key: string;
  /** issue #29 の大項目番号。ロードマップとの対応を追跡可能にする */
  readonly issueSection: number;
  readonly icon: LucideIcon;
  readonly status: NavItemStatus;
  /** status が "enabled" のとき必須 */
  readonly to?: string;
  /** 全て満たさないユーザーには表示しない（permissions-check ベース） */
  readonly requires?: readonly {
    readonly uri: string;
    readonly method: HttpMethod;
  }[];
  readonly children?: readonly NavItem[];
};

export type NavSection = {
  /** i18n キー（nav:<titleKey>） */
  readonly titleKey:
    | "sectionBusiness"
    | "sectionAdmin"
    | "sectionDesign"
    | "sectionSettings";
  readonly items: readonly NavItem[];
};

/**
 * サイドバーの項目定義。ラベル・順序・大項目番号は issue #29 の
 * 「大項目 → 中項目（実装バックログ）」をそのまま転記している。
 * 項目を増やす／status を変えるときは issue 側も更新すること。
 *
 * P1 は大項目 2「マイ案件」のみ enabled。他は planned（ロードマップとして見せる）。
 */
export const NAV_SECTIONS: readonly NavSection[] = [
  {
    titleKey: "sectionBusiness",
    items: [
      {
        key: "home",
        issueSection: 1,
        icon: Home,
        status: "enabled",
        to: ROUTES.home,
      },
      {
        key: "myApplications",
        issueSection: 2,
        icon: FileText,
        status: "enabled",
        to: ROUTES.myApplications,
        requires: [
          { uri: PERMISSION_TARGETS.processInstanceListForMe, method: "POST" },
        ],
      },
      {
        key: "approvalRequests",
        issueSection: 3,
        icon: ClipboardCheck,
        status: "enabled",
        to: ROUTES.approvalRequests,
        requires: [{ uri: PERMISSION_TARGETS.taskList, method: "GET" }],
      },
      {
        key: "notifications",
        issueSection: 4,
        icon: Bell,
        status: "enabled",
        to: ROUTES.notifications,
      },
      {
        key: "permissions",
        issueSection: 6,
        icon: Users,
        status: "enabled",
        to: ROUTES.permissions,
        requires: [{ uri: PERMISSION_TARGETS.userSearch, method: "GET" }],
      },
    ],
  },
  {
    titleKey: "sectionAdmin",
    items: [
      {
        key: "allCases",
        issueSection: 5,
        icon: Monitor,
        status: "enabled",
        to: ROUTES.allCases,
        // 一覧だけでなく**個別案件を開ける**ことを条件にする。
        // 一覧の権限だけで出すと「並ぶのに開けない（403）」状態になる
        requires: [
          { uri: PERMISSION_TARGETS.processInstanceDetail, method: "GET" },
        ],
      },
      {
        key: "activityLog",
        issueSection: 7,
        icon: ScrollText,
        to: ROUTES.activityLog,
        status: "enabled",
        // 全案件モニタと同じ理由（開けない案件を並べない）
        requires: [
          { uri: PERMISSION_TARGETS.processInstanceDetail, method: "GET" },
        ],
      },
    ],
  },
  {
    titleKey: "sectionDesign",
    items: [
      {
        key: "processDesign",
        issueSection: 8,
        icon: Workflow,
        status: "enabled",
        to: ROUTES.processDesign,
        requires: [
          { uri: PERMISSION_TARGETS.processModelList, method: "POST" },
        ],
      },
    ],
  },
  {
    titleKey: "sectionSettings",
    items: [
      {
        key: "secrets",
        issueSection: 10,
        icon: KeyRound,
        status: "enabled",
        to: ROUTES.secrets,
        requires: [{ uri: PERMISSION_TARGETS.secretList, method: "GET" }],
      },
    ],
  },
];
